import { sectionShellMobileNavigationVariants } from '@/shared/config/sectionShell';
import { useIsMobile } from '@/shared/hooks/useIsMobile';
import { resolveMobileSection } from '@/shared/lib/mobileSection';
import { findActiveRouteSecondarySidebar } from '@/shared/lib/routeSecondarySidebar';
import { useLocation, useMatches } from '@tanstack/react-router';
import type { ReactNode } from 'react';

/**
 * Two-rail section layout: an optional secondary sidebar beside the section's
 * content. A section's layout route renders it as
 * `<SectionShell sidebar={<XSidebar/>}><Outlet/></SectionShell>`.
 *
 * The rail mounts only within that section (and persists across its child
 * navigations), while the persistent primary nav stays in `AppLayout` at the
 * `_authenticated/_app` layout — so navigating never reloads the primary, and the
 * secondary loads only where a section provides one. On mobile, a section root
 * presents that same sidebar as its secondary-navigation page; child routes show
 * their outlet and the app top bar returns to the root. A shell without a sidebar
 * is the standard content wrapper for pages with only primary navigation.
 * A child route can declare `staticData.sectionShell.secondarySidebar` to replace
 * the fallback sidebar, or set it to `null` to hide the sidebar for that route.
 * Width is the shared `--secondary-sidebar-width` token so every rail matches.
 *
 * The sidebar stays mounted while a mobile child page is open, preserving its
 * search and expansion state for the Back-to-selection transition.
 */
export function SectionShell({
  sidebar,
  children,
}: {
  sidebar?: ReactNode;
  children: ReactNode;
}) {
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const activeRouteSidebar = findActiveRouteSecondarySidebar(useMatches());
  const RouteSidebar = activeRouteSidebar?.sidebar;
  const resolvedSidebar = activeRouteSidebar ? (
    RouteSidebar ? (
      <RouteSidebar key={activeRouteSidebar.key} />
    ) : undefined
  ) : (
    sidebar
  );
  const { isSectionRoot } = resolveMobileSection(pathname);
  const hasSidebar = resolvedSidebar != null;
  const showMobileNavigation = isMobile && hasSidebar && isSectionRoot;

  return (
    <div className="flex min-h-0 min-w-0 flex-1">
      {!isMobile && hasSidebar ? (
        <aside
          data-slot="secondary-sidebar"
          className="sticky top-0 flex h-svh w-[var(--secondary-sidebar-width)] shrink-0 flex-col border-sidebar-border border-r bg-sidebar text-sidebar-foreground"
        >
          {resolvedSidebar}
        </aside>
      ) : null}
      {isMobile && hasSidebar ? (
        <div className={sectionShellMobileNavigationVariants({ active: showMobileNavigation })}>
          {resolvedSidebar}
        </div>
      ) : null}
      {!showMobileNavigation ? (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">{children}</div>
      ) : null}
    </div>
  );
}
